import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import BillingService from '../services/billingService';
import { 
  ArrowTopRightOnSquareIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';

const BillingPage: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Automatically redirect to Stripe Customer Portal
    redirectToStripePortal();
  }, []);

  const redirectToStripePortal = async () => {
    try {
      setLoading(true);
      setRedirecting(true);
      
      // Redirect to Stripe Customer Portal
      await BillingService.redirectToCustomerPortal();
    } catch (error) {
      console.error('Error redirecting to Stripe Customer Portal:', error);
      
      // Check if it's a 405 error (endpoint not available yet)
      if (error instanceof Error && error.message.includes('Method Not Allowed')) {
        setError('Billing portal is temporarily unavailable. Please try again in a few minutes after the backend update completes.');
      } else {
        setError('Failed to open billing portal. Please try again.');
      }
      setRedirecting(false);
    } finally {
      setLoading(false);
    }
  };

  if (loading || redirecting) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">
            {redirecting ? 'Redirecting to Stripe Customer Portal...' : 'Loading...'}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
              <ShieldCheckIcon className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-medium text-red-900 mb-2">
              Unable to Access Billing Portal
            </h3>
            <p className="text-red-700 mb-4">{error}</p>
            <div className="space-y-3">
              <button
                onClick={redirectToStripePortal}
                className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                Try Again
              </button>
              <a
                href="/dashboard"
                className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                Return to Dashboard
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // This should not be reached as we redirect immediately
  return null;
};

export default BillingPage;